import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, Instagram, ArrowLeft, ShoppingBag, Package, Globe, Zap, Store, Palette, Building, Wifi } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import PublishTiendaNubeModal from '../components/PublishTiendaNubeModal';
import PublishInstagramModal from '../components/PublishInstagramModal';
import MassPublishingEcommerceModal from '../components/MassPublishingEcommerceModal';
import MassPublishingSocialModal from '../components/MassPublishingSocialModal';
import { useAuth } from '../contexts/AuthContext';
import RequireAuthModal from '../components/auth/RequireAuthModal';
import LoginModal from '../components/auth/LoginModal';
import { useEcommercePlatforms } from '../hooks/useEcommercePlatforms';
import { useSocialMediaPlatforms } from '../hooks/useSocialMediaPlatforms';

const PublishWithAI: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  const { activePlatforms } = useEcommercePlatforms();
  const { activePlatforms: activeSocialPlatforms } = useSocialMediaPlatforms();
  const [showTiendaNubeModal, setShowTiendaNubeModal] = useState(false);
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [showMassEcommerceModal, setShowMassEcommerceModal] = useState(false);
  const [showMassSocialModal, setShowMassSocialModal] = useState(false);
  const [showRequireAuth, setShowRequireAuth] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Función para obtener el ícono y color de cada plataforma
  const getPlatformConfig = (platformId: string) => {
    const configs: Record<string, { icon: React.ReactNode, color: string, bgColor: string }> = {
      // E-commerce platforms
      mercadolibre: {
        icon: <ShoppingBag className="w-6 h-6" />,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
      },
      amazon: {
        icon: <Package className="w-6 h-6" />,
        color: 'text-orange-500',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30'
      },
      shopify: {
        icon: <Store className="w-6 h-6" />,
        color: 'text-green-500',
        bgColor: 'bg-green-100 dark:bg-green-900/30'
      },
      tiendanube: {
        icon: <Cloud className="w-6 h-6" />,
        color: 'text-blue-500',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30'
      },
      prestashop: {
        icon: <Zap className="w-6 h-6" />,
        color: 'text-purple-500',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30'
      },
      magento: {
        icon: <Building className="w-6 h-6" />,
        color: 'text-red-500',
        bgColor: 'bg-red-100 dark:bg-red-900/30'
      },
      wix: {
        icon: <Palette className="w-6 h-6" />,
        color: 'text-pink-500',
        bgColor: 'bg-pink-100 dark:bg-pink-900/30'
      },
      bigcommerce: {
        icon: <Globe className="w-6 h-6" />,
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-100 dark:bg-indigo-900/30'
      },
      vtex: {
        icon: <Wifi className="w-6 h-6" />,
        color: 'text-cyan-500',
        bgColor: 'bg-cyan-100 dark:bg-cyan-900/30'
      },
      // Social media platforms
      instagram: {
        icon: <span className="text-xl">📸</span>,
        color: 'text-pink-500',
        bgColor: 'bg-pink-100 dark:bg-pink-900/30'
      },
      facebook: {
        icon: <span className="text-xl">📘</span>,
        color: 'text-blue-500',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30'
      },
      tiktok: {
        icon: <span className="text-xl">🎵</span>,
        color: 'text-black dark:text-white',
        bgColor: 'bg-gray-100 dark:bg-gray-900/30'
      }
    };
    return configs[platformId] || { icon: <ShoppingBag className="w-6 h-6" />, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-900/30' };
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 15,
      },
    },
    hover: {
      scale: 1.05,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 10,
      },
    },
    tap: {
      scale: 0.95,
    },
  };

  return (
    <div className="min-h-screen px-4 pt-20 pb-24">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 mb-6"
          >
            <ArrowLeft size={20} />
            <span>{t('back')}</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-contrail font-bold text-gray-900 dark:text-gray-100">
            {t('publishWithAI')}
          </h1>
        </motion.div>

        {/* Botones principales de publicación masiva */}
        <motion.div variants={itemVariants} className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Botón E-commerce */}
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => {
                if (!user) {
                  setShowRequireAuth(true);
                  return;
                }
                setShowMassEcommerceModal(true);
              }}
              className="card p-8 text-left group hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-2xl">
                  <ShoppingBag className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {t('publishToMyStores')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('publishToMyStoresDescription')}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                  {activePlatforms.length > 0 ? (
                    `${activePlatforms.length} ${activePlatforms.length === 1 ? t('platformsConnected') : t('platformsConnectedPlural')}`
                  ) : (
                    t('connectYourPlatforms')
                  )}
                </div>
                <div className="text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform duration-200">
                  →
                </div>
              </div>
            </motion.button>

            {/* Botón Redes Sociales */}
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => {
                if (!user) {
                  setShowRequireAuth(true);
                  return;
                }
                setShowMassSocialModal(true);
              }}
              className="card p-8 text-left group hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-800"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-4 bg-pink-100 dark:bg-pink-900/30 rounded-2xl">
                  <Globe className="w-8 h-8 text-pink-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {t('publishToMyNetworks')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {t('publishToMyNetworksDescription')}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-pink-600 dark:text-pink-400 font-medium">
                  {activeSocialPlatforms.length > 0 ? (
                    `${activeSocialPlatforms.length} ${activeSocialPlatforms.length === 1 ? t('networksConnected') : t('networksConnectedPlural')}`
                  ) : (
                    t('connectYourNetworks')
                  )}
                </div>
                <div className="text-pink-600 dark:text-pink-400 group-hover:translate-x-1 transition-transform duration-200">
                  →
                </div>
              </div>
            </motion.button>
          </div>
          
          {/* Mensaje de estado dinámico - ocupa toda la sección */}
          {(activePlatforms.length === 0 || activeSocialPlatforms.length === 0) && (
            <motion.div 
              variants={itemVariants}
              className="mt-6"
            >
              <div className="w-full flex items-center justify-center px-6 py-4 bg-red-50/80 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl backdrop-blur-sm">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                  {activePlatforms.length === 0 && activeSocialPlatforms.length === 0
                    ? t('noPlatformsConnected')
                    : activePlatforms.length === 0
                    ? t('noStoresConnected')
                    : t('noSocialNetworksConnected')
                  }
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Sección E-commerce */}
        <motion.div variants={itemVariants} className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('ecommercePlatforms')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t('ecommercePlatformsDescription')}
          </p>

          {activePlatforms.length > 0 ? (
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {activePlatforms.map((platform) => {
                const config = getPlatformConfig(platform.id);
                return (
                  <motion.button
                    key={platform.id}
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    onClick={() => {
                      if (!user) {
                        setShowRequireAuth(true);
                        return;
                      }
                      // Por ahora abrimos el modal de TiendaNube para todas las plataformas
                      // En una implementación real, cada plataforma tendría su propio modal
                      if (platform.id === 'tiendanube') {
                        setShowTiendaNubeModal(true);
                      } else {
                        alert(`${platform.name} ${t('willBeAvailableSoon')}`);
                      }
                    }}
                    className="card p-6 text-left group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4 mb-3">
                      <div className={`p-3 rounded-xl ${config.bgColor}`}>
                        <div className={config.color}>
                          {config.icon}
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {platform.name}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {platform.description}
                    </p>
                    <div className="mt-3 flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        {t('connected')}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              variants={itemVariants}
              className="card p-8 text-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800"
            >
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-2xl w-fit mx-auto mb-4">
                <ShoppingBag className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('connectYourFavoriteStores')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('connectYourFavoriteStoresDescription')}
              </p>
              <button
                onClick={() => navigate('/settings')}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
              >
{t('manageAccount')}
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Sección Redes Sociales */}
        <motion.div variants={itemVariants} className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('socialMedia')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t('socialMediaDescription')}
          </p>

          {activeSocialPlatforms.length > 0 ? (
            <motion.div
              variants={itemVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {activeSocialPlatforms.map((platform) => {
                const config = getPlatformConfig(platform.id);
                return (
                  <motion.button
                    key={`social-${platform.id}`}
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    onClick={() => {
                      if (!user) {
                        setShowRequireAuth(true);
                        return;
                      }
                      // Por ahora abrimos el modal de Instagram para todas las redes sociales
                      // En una implementación real, cada red social tendría su propio modal
                      if (platform.id === 'instagram') {
                        setShowInstagramModal(true);
                      } else {
                        alert(`${platform.name} ${t('willBeAvailableSoon')}`);
                      }
                    }}
                    className="card p-6 text-left group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-center space-x-4 mb-3">
                      <div className={`p-3 rounded-xl ${config.bgColor}`}>
                        <div className={config.color}>
                          {config.icon}
                        </div>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {platform.name}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {platform.description}
                    </p>
                    <div className="mt-3 flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                        {t('connected')}
                      </span>
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              variants={itemVariants}
              className="card p-8 text-center bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border-pink-200 dark:border-pink-800"
            >
              <div className="p-4 bg-pink-100 dark:bg-pink-900/30 rounded-2xl w-fit mx-auto mb-4">
                <Globe className="w-12 h-12 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {t('connectYourFavoriteNetworks')}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {t('connectYourFavoriteNetworksDescription')}
              </p>
              <button
                onClick={() => navigate('/settings')}
                className="px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors duration-200 font-medium"
              >
{t('manageAccount')}
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Modals */}
        <AnimatePresence>
          {showTiendaNubeModal && (
            <PublishTiendaNubeModal
              isOpen={showTiendaNubeModal}
              onClose={() => setShowTiendaNubeModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showInstagramModal && (
            <PublishInstagramModal
              isOpen={showInstagramModal}
              onClose={() => setShowInstagramModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showMassEcommerceModal && (
            <MassPublishingEcommerceModal
              isOpen={showMassEcommerceModal}
              onClose={() => setShowMassEcommerceModal(false)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showMassSocialModal && (
            <MassPublishingSocialModal
              isOpen={showMassSocialModal}
              onClose={() => setShowMassSocialModal(false)}
            />
          )}
        </AnimatePresence>

        <RequireAuthModal
          isOpen={showRequireAuth}
          onClose={() => setShowRequireAuth(false)}
          onLoginClick={() => { setShowRequireAuth(false); setShowLoginModal(true); }}
        />
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </motion.div>
    </div>
  );
};

export default PublishWithAI;
